import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Image } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api, { uploadSlideImage, getSuggerimentiRegisti, getSuggerimentiAttori, getSuggerimentiCaseFilm } from '../../services/api';

const EMPTY_SLIDE = {
    image_url: '', mobile_image_url: '', badge: '', title: '', highlight: '', subtitle: '',
    link_text: 'Scopri di più', link_url: '', link_style: 'link', button_color: '#dc2626', text_color: '#1f2937', highlight_color: '#dc2626', background_color: '#e5e7eb',
};

const BENEFIT_ICONS = [
    ['truck', 'Camioncino (spedizione)'], ['shield', 'Scudo (garanzia)'], ['star', 'Stella (fedeltà)'],
    ['store', 'Negozio (ritiro)'], ['headset', 'Assistenza'], ['gift', 'Regalo'],
    ['tag', 'Etichetta (offerte)'], ['heart', 'Cuore'], ['card', 'Pagamenti'],
];

const DEFAULT_BENEFITS = [
    { icon: 'truck', title: 'Spedizione gratuita', text: 'In tutta Italia', link_url: '' },
    { icon: 'shield', title: 'Usato garantito', text: 'Ogni usato è controllato', link_url: '' },
    { icon: 'star', title: 'Programma fedeltà', text: 'Accumula punti a ogni acquisto', link_url: '/programma-fedelta' },
    { icon: 'store', title: 'Ritiro a Modena', text: 'Su appuntamento', link_url: '' },
];

const slidePreviewUrl =(url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const base = process.env.REACT_APP_STATIC_FILES_BASE_URL || 'http://localhost:8000';
    return base + (url.startsWith('/uploads/') ? '/api' + url : url);
};

const HomepageBlockForm = ({ show, handleClose, block, onSave }) => {
    const [formData, setFormData] = useState({});
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [mobileImageFile, setMobileImageFile] = useState(null);
    const [mobilePreviewUrl, setMobilePreviewUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filterOptions, setFilterOptions] = useState({
        genres: [],
        supportos: [],
        conditions: [],
        categorie_prodotto: [],
        brands: [],
        licenze: [],
        personaggi: []
    });
    const [filmSuggestions, setFilmSuggestions] = useState({ registi: [], attori: [], case: [] });
    const isNew = !block;

    useEffect(() => {
        if (block) {
            setFormData(block);
            if (block.content?.image_url) {
                const staticBaseUrl = process.env.REACT_APP_STATIC_FILES_BASE_URL || 'http://localhost:8000';
                let imageUrl = block.content.image_url;
                // Logica robusta: aggiunge /api se mancante per i percorsi di upload
                if (imageUrl.startsWith('/uploads/') && !imageUrl.startsWith('/api/uploads/')) {
                    imageUrl = '/api' + imageUrl;
                }
                setPreviewUrl(staticBaseUrl + imageUrl);
            } else {
                setPreviewUrl(null);
            }
            if (block.content?.mobile_image_url) {
                const staticBaseUrl = process.env.REACT_APP_STATIC_FILES_BASE_URL || 'http://localhost:8000';
                let mobileUrl = block.content.mobile_image_url;
                if (mobileUrl.startsWith('/uploads/') && !mobileUrl.startsWith('/api/uploads/')) {
                    mobileUrl = '/api' + mobileUrl;
                }
                setMobilePreviewUrl(staticBaseUrl + mobileUrl);
            } else {
                setMobilePreviewUrl(null);
            }
        } else {
            // Reset form per un nuovo blocco
            setFormData({
                title: '',
                block_type: 'hero_banner',
                is_active: true,
                order: 0,
                content: {}
            });
            setPreviewUrl(null);
            setMobilePreviewUrl(null);
        }
        // Resetta il file su ogni apertura/cambio di blocco
        setImageFile(null);
        setMobileImageFile(null);
    }, [block, show]);

    // Inizializza i valori di default per product_carousel e product_gallery
    useEffect(() => {
        if (formData.block_type === 'benefits_bar' && !formData.content?.items) {
            setFormData(prev => ({ ...prev, content: { ...prev.content, items: DEFAULT_BENEFITS } }));
        }
        if (formData.block_type === 'product_carousel' && !formData.content?.product_type) {
            setFormData(prev => ({
                ...prev,
                content: {
                    ...prev.content,
                    product_type: 'film',
                    selection_mode: 'showcase'
                }
            }));
        }
        if (formData.block_type === 'product_gallery' && !formData.content?.product_type) {
            setFormData(prev => ({
                ...prev,
                content: {
                    ...prev.content,
                    product_type: 'film',
                    selection_mode: 'latest',
                    limit: 6
                }
            }));
        }
    }, [formData.block_type]);

    // Carica le opzioni dei filtri quando product_type cambia
    useEffect(() => {
        const productType = formData.content?.product_type;
        if (productType && (formData.block_type === 'product_carousel' || formData.block_type === 'product_gallery')) {
            loadFilterOptions(productType);
        }
    }, [formData.content?.product_type, formData.block_type]);

    // Elenchi per l'autocompletamento dei campi testo (regista, attori, casa) del filtro film.
    useEffect(() => {
        if (formData.content?.product_type !== 'film' || !['product_gallery', 'product_carousel'].includes(formData.block_type)) return;
        const load = (request) => request()
            .then(r => Array.isArray(r.data) ? r.data : [])
            .catch(() => []);
        Promise.all([load(getSuggerimentiRegisti), load(getSuggerimentiAttori), load(getSuggerimentiCaseFilm)])
            .then(([registi, attori, caseList]) => setFilmSuggestions({ registi, attori, case: caseList }));
    }, [formData.content?.product_type, formData.block_type]);

    const loadFilterOptions = async (productType) => {

        try {
            switch (productType) {
                case 'film':
                    try {
                        const [filmGenres, filmSupports, filmConditions] = await Promise.all([
                            api.get('/ecommerce/films/filters/genres').then(r => r.data).catch(() => []),
                            api.get('/ecommerce/films/filters/supportos').then(r => r.data).catch(() => []),
                            api.get('/ecommerce/films/filters/conditions').then(r => r.data).catch(() => [])
                        ]);
                        console.log('Film filters loaded:', { genres: filmGenres, supports: filmSupports, conditions: filmConditions });
                        setFilterOptions({
                            genres: Array.isArray(filmGenres) ? filmGenres : [],
                            supportos: Array.isArray(filmSupports) ? filmSupports : [],
                            conditions: Array.isArray(filmConditions) ? filmConditions : [],
                            categorie_prodotto: [],
                            brands: [],
                            licenze: [],
                            personaggi: []
                        });
                    } catch (error) {
                        console.error('Error loading film filters:', error);
                    }
                    break;
                
                case 'musica':
                    try {
                        const [musicaGenres, musicaSupports, musicaConditions] = await Promise.all([
                            api.get('/ecommerce/musica/filters/genres').then(r => r.data).catch(() => []),
                            api.get('/ecommerce/musica/filters/supportos').then(r => r.data).catch(() => []),
                            api.get('/ecommerce/musica/filters/conditions').then(r => r.data).catch(() => [])
                        ]);
                        setFilterOptions({
                            genres: Array.isArray(musicaGenres) ? musicaGenres : [],
                            supportos: Array.isArray(musicaSupports) ? musicaSupports : [],
                            conditions: Array.isArray(musicaConditions) ? musicaConditions : [],
                            categorie_prodotto: [],
                            brands: [],
                            licenze: [],
                            personaggi: []
                        });
                    } catch (error) {
                        console.error('Error loading musica filters:', error);
                    }
                    break;
                
                case 'collectible':
                    try {
                        const [categorieProdotto, brands, licenze, personaggi] = await Promise.all([
                            api.get('/ecommerce/collectibles/filters/categorie_prodotto').then(r => r.data).catch(() => []),
                            api.get('/ecommerce/collectibles/filters/brands').then(r => r.data).catch(() => []),
                            api.get('/ecommerce/collectibles/filters/licenze').then(r => r.data).catch(() => []),
                            api.get('/ecommerce/collectibles/filters/personaggi').then(r => r.data).catch(() => [])
                        ]);
                        setFilterOptions({
                            genres: [],
                            supportos: [],
                            conditions: [],
                            categorie_prodotto: Array.isArray(categorieProdotto) ? categorieProdotto : [],
                            brands: Array.isArray(brands) ? brands : [],
                            licenze: Array.isArray(licenze) ? licenze : [],
                            personaggi: Array.isArray(personaggi) ? personaggi : []
                        });
                    } catch (error) {
                        console.error('Error loading collectibles filters:', error);
                        setFilterOptions({
                            genres: [],
                            supportos: [],
                            conditions: [],
                            categorie_prodotto: [],
                            brands: [],
                            licenze: [],
                            personaggi: []
                        });
                    }
                    break;
                
                case 'servizi':
                    try {
                        const [serviziSupports, serviziCategories] = await Promise.all([
                            api.get('/servizi/suggerimenti/supporti').then(r => r.data).catch(() => []),
                            api.get('/servizi/suggerimenti/categorie').then(r => r.data).catch(() => [])
                        ]);
                        setFilterOptions({
                            genres: [],
                            supportos: Array.isArray(serviziSupports) ? serviziSupports : [],
                            conditions: [],
                            categorie_prodotto: Array.isArray(serviziCategories) ? serviziCategories : [],
                            brands: [],
                            licenze: [],
                            personaggi: []
                        });
                    } catch (error) {
                        console.error('Error loading servizi filters:', error);
                    }
                    break;
                
                default:
                    break;
            }
        } catch (error) {
            console.error('Error loading filter options:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleContentChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            content: {
                ...prev.content,
                [name]: value
            }
        }));
    };

    const handleFilterChange = (filterName, value) => {
        setFormData(prev => ({
            ...prev,
            content: {
                ...prev.content,
                filters: {
                    ...prev.content?.filters,
                    [filterName]: value === false ? false : (value || undefined)
                }
            }
        }));
    };

    const slides = formData.content?.slides || [];

    const setSlides = (next) => setFormData(prev => ({ ...prev, content: { ...prev.content, slides: next } }));

    const updateSlide = (idx, patch) => setSlides(slides.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

    const moveSlide = (idx, dir) => {
        const j = idx + dir;
        if (j < 0 || j >= slides.length) return;
        const next = [...slides];
        [next[idx], next[j]] = [next[j], next[idx]];
        setSlides(next);
    };

    const handleSlideImage = async (idx, field, file) => {
        if (!file) return;
        try {
            const res = await uploadSlideImage(file);
            updateSlide(idx, { [field]: res.data.url });
        } catch (err) {
            console.error('Errore upload immagine slide:', err);
            toast.error("Errore nel caricamento dell'immagine.");
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleMobileFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setMobileImageFile(file);
            setMobilePreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onSave(formData, imageFile, mobileImageFile); // Passa anche i file
            toast.success(isNew ? "Blocco creato con successo!" : "Blocco aggiornato con successo!");
            handleClose();
        } catch (err) {
            console.error("Errore nel salvataggio del blocco:", err);
            toast.error("Errore nel salvataggio del blocco.");
        } finally {
            setLoading(false);
        }
    };

    const renderContentFields = () => {
        switch (formData.block_type) {
            case 'hero_banner':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Titolo Hero</Form.Label>
                            <Form.Control type="text" name="hero_title" value={formData.content?.hero_title || ''} onChange={handleContentChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Sottotitolo Hero</Form.Label>
                            <Form.Control type="text" name="hero_subtitle" value={formData.content?.hero_subtitle || ''} onChange={handleContentChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Immagine Desktop</Form.Label>
                            <Form.Control type="file" name="image_file" onChange={handleFileChange} accept="image/*" />
                            {formData.content?.image_url && (
                                <Form.Group className="mt-2">
                                    <Form.Label>Percorso Immagine Salvato (Debug)</Form.Label>
                                    <Form.Control type="text" readOnly value={formData.content.image_url} />
                                </Form.Group>
                            )}
                            {previewUrl && <Image src={previewUrl} thumbnail fluid className="mt-3" style={{ maxHeight: '200px' }} />}
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Immagine Mobile <span className="text-muted">(opzionale — ottimizzata per smartphone)</span></Form.Label>
                            <Form.Control type="file" name="mobile_image_file" onChange={handleMobileFileChange} accept="image/*" />
                            {formData.content?.mobile_image_url && (
                                <Form.Group className="mt-2">
                                    <Form.Label>Percorso Immagine Mobile Salvato (Debug)</Form.Label>
                                    <Form.Control type="text" readOnly value={formData.content.mobile_image_url} />
                                </Form.Group>
                            )}
                            {mobilePreviewUrl && <Image src={mobilePreviewUrl} thumbnail fluid className="mt-3" style={{ maxHeight: '200px' }} />}
                        </Form.Group>
                        <hr/>
                        <h6>Stile e Layout</h6>
                        <Form.Group className="mb-3">
                            <Form.Label>Modalità Immagine Sfondo</Form.Label>
                            <Form.Select name="background_size_mode" value={formData.content?.background_size_mode || 'cover'} onChange={handleContentChange}>
                                <option value="cover">Copri (Cover) - Riempie, ritaglia se necessario</option>
                                <option value="contain">Contieni (Contain) - Immagine intera, lascia bordi se necessario</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Altezza Banner</Form.Label>
                            <Form.Select name="height_preset" value={formData.content?.height_preset || 'medium'} onChange={handleContentChange}>
                                <option value="mini">Mini (80px)</option>
                                <option value="extra_small">Extra Small (120px)</option>
                                <option value="slim">Slim (240px)</option>
                                <option value="medium">Media (360px)</option>
                                <option value="large">Grande (500px)</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Colore Sfondo</Form.Label>
                            <Form.Control
                                type="color"
                                name="background_color"
                                value={formData.content?.background_color || '#000000'}
                                onChange={handleContentChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Colore Testo</Form.Label>
                            <Form.Control
                                type="color"
                                name="text_color"
                                value={formData.content?.text_color || '#FFFFFF'}
                                onChange={handleContentChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Famiglia Font</Form.Label>
                            <Form.Select
                                name="font_family"
                                value={formData.content?.font_family || 'Default'}
                                onChange={handleContentChange}
                            >
                                <option value="Default">Default (sito)</option>
                                <option value="Arial, sans-serif">Arial</option>
                                <option value="Verdana, sans-serif">Verdana</option>
                                <option value="Georgia, serif">Georgia</option>
                                <option value="'Times New Roman', serif">Times New Roman</option>
                                <option value="'Courier New', monospace">Courier New</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Dimensione Font</Form.Label>
                            <Form.Select
                                name="font_size_preset"
                                value={formData.content?.font_size_preset || 'Default'}
                                onChange={handleContentChange}
                            >
                                <option value="Default">Default</option>
                                <option value="text-sm">Small</option>
                                <option value="text-base">Base</option>
                                <option value="text-lg">Large</option>
                                <option value="text-xl">Extra Large</option>
                                <option value="text-2xl">2XL</option>
                                <option value="text-3xl">3XL</option>
                                <option value="text-4xl">4XL</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Opacità Overlay (0 = trasparente, 100 = coprente)</Form.Label>
                            <Form.Range
                                name="overlay_opacity"
                                min="0"
                                max="100"
                                value={formData.content?.overlay_opacity || 50}
                                onChange={handleContentChange}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                id="use_shadow-switch"
                                label="Usa Ombra sul Banner"
                                name="use_shadow"
                                checked={formData.content?.use_shadow || false}
                                onChange={e => handleContentChange({ target: { name: 'use_shadow', value: e.target.checked } })}
                            />
                        </Form.Group>
                        <hr/>
                        <h6>Pulsante (Call to Action)</h6>
                        <Form.Group className="mb-3">
                            <Form.Label>Testo Pulsante</Form.Label>
                            <Form.Control type="text" name="button_text" value={formData.content?.button_text || ''} onChange={handleContentChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Link Pulsante</Form.Label>
                            <Form.Control type="text" name="button_link" value={formData.content?.button_link || ''} onChange={handleContentChange} />
                        </Form.Group>
                    </>
                );
            case 'benefits_bar': {
                const items = formData.content?.items || [];
                const setItems = (next) => setFormData(prev => ({ ...prev, content: { ...prev.content, items: next } }));
                const updateItem = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
                return (
                    <>
                        <Form.Text className="d-block mb-3">Fascia con 2-4 vantaggi in icone (spedizione, garanzia, fedeltà...). Si consiglia di metterla subito sotto il carosello.</Form.Text>
                        {items.map((item, idx) => (
                            <div key={idx} className="border rounded p-3 mb-3 bg-light">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong>Vantaggio {idx + 1}</strong>
                                    <Button size="sm" variant="outline-danger" onClick={() => setItems(items.filter((_, i) => i !== idx))}>Elimina</Button>
                                </div>
                                <div className="row">
                                    <div className="col-md-4">
                                        <Form.Label className="small mb-0">Icona</Form.Label>
                                        <Form.Select size="sm" value={item.icon || 'star'} onChange={(e) => updateItem(idx, { icon: e.target.value })}>
                                            {BENEFIT_ICONS.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
                                        </Form.Select>
                                    </div>
                                    <div className="col-md-8">
                                        <Form.Label className="small mb-0">Titolo</Form.Label>
                                        <Form.Control size="sm" value={item.title || ''} onChange={(e) => updateItem(idx, { title: e.target.value })} />
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-7">
                                        <Form.Label className="small mb-0">Testo</Form.Label>
                                        <Form.Control size="sm" value={item.text || ''} onChange={(e) => updateItem(idx, { text: e.target.value })} />
                                    </div>
                                    <div className="col-md-5">
                                        <Form.Label className="small mb-0">Link (opzionale)</Form.Label>
                                        <Form.Control size="sm" value={item.link_url || ''} onChange={(e) => updateItem(idx, { link_url: e.target.value })} placeholder="/programma-fedelta" />
                                    </div>
                                </div>
                            </div>
                        ))}
                        {items.length < 4 && (
                            <Button variant="outline-primary" onClick={() => setItems([...items, { icon: 'star', title: '', text: '', link_url: '' }])}>+ Aggiungi vantaggio</Button>
                        )}
                    </>
                );
            }
            case 'hero_carousel':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Cambio automatico slide (secondi, 0 = disattivato)</Form.Label>
                            <Form.Control
                                type="number" min="0" max="30"
                                value={Math.round((formData.content?.autoplay_ms ?? 6000) / 1000)}
                                onChange={(e) => handleContentChange({ target: { name: 'autoplay_ms', value: (parseInt(e.target.value) || 0) * 1000 } })}
                            />
                        </Form.Group>
                        <Form.Text className="d-block mb-3">
                            Immagine desktop consigliata 1920×600, mobile 800×800. Il testo sta a sinistra, l'immagine a destra: lascia libera la parte sinistra dell'immagine o usa un'immagine con sfondo uniforme.
                        </Form.Text>
                        {slides.map((slide, idx) => (
                            <div key={idx} className="border rounded p-3 mb-3 bg-light">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong>Slide {idx + 1}</strong>
                                    <div>
                                        <Button size="sm" variant="outline-secondary" className="me-1" disabled={idx === 0} onClick={() => moveSlide(idx, -1)}>↑</Button>
                                        <Button size="sm" variant="outline-secondary" className="me-1" disabled={idx === slides.length - 1} onClick={() => moveSlide(idx, 1)}>↓</Button>
                                        <Button size="sm" variant="outline-danger" onClick={() => setSlides(slides.filter((_, i) => i !== idx))}>Elimina</Button>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small mb-0">Badge (es. "Fino al 31 ottobre")</Form.Label>
                                            <Form.Control size="sm" value={slide.badge || ''} onChange={(e) => updateSlide(idx, { badge: e.target.value })} />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small mb-0">Testo evidenziato (es. "-20%")</Form.Label>
                                            <Form.Control size="sm" value={slide.highlight || ''} onChange={(e) => updateSlide(idx, { highlight: e.target.value })} />
                                        </Form.Group>
                                    </div>
                                </div>
                                <Form.Group className="mb-2">
                                    <Form.Label className="small mb-0">Titolo</Form.Label>
                                    <Form.Control size="sm" value={slide.title || ''} onChange={(e) => updateSlide(idx, { title: e.target.value })} />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label className="small mb-0">Sottotitolo</Form.Label>
                                    <Form.Control size="sm" value={slide.subtitle || ''} onChange={(e) => updateSlide(idx, { subtitle: e.target.value })} />
                                </Form.Group>
                                <div className="row">
                                    <div className="col-md-6">
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small mb-0">Testo link</Form.Label>
                                            <Form.Control size="sm" value={slide.link_text || ''} onChange={(e) => updateSlide(idx, { link_text: e.target.value })} />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small mb-0">Link (es. /offerte, /films?support=4K)</Form.Label>
                                            <Form.Control size="sm" value={slide.link_url || ''} onChange={(e) => updateSlide(idx, { link_url: e.target.value })} />
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <div className="col-md-6">
                                        <Form.Label className="small mb-0">Stile link</Form.Label>
                                        <Form.Select size="sm" value={slide.link_style || 'link'} onChange={(e) => updateSlide(idx, { link_style: e.target.value })}>
                                            <option value="link">Sottolineato</option>
                                            <option value="button">Pulsante</option>
                                        </Form.Select>
                                    </div>
                                    {slide.link_style === 'button' && (
                                        <div className="col-md-6">
                                            <Form.Label className="small mb-0">Colore pulsante</Form.Label>
                                            <Form.Control type="color" size="sm" value={slide.button_color || '#dc2626'} onChange={(e) => updateSlide(idx, { button_color: e.target.value })} />
                                        </div>
                                    )}
                                </div>
                                <div className="row">
                                    <div className="col-4">
                                        <Form.Label className="small mb-0">Colore testo</Form.Label>
                                        <Form.Control type="color" size="sm" value={slide.text_color || '#1f2937'} onChange={(e) => updateSlide(idx, { text_color: e.target.value })} />
                                    </div>
                                    <div className="col-4">
                                        <Form.Label className="small mb-0">Colore evidenziato</Form.Label>
                                        <Form.Control type="color" size="sm" value={slide.highlight_color || '#dc2626'} onChange={(e) => updateSlide(idx, { highlight_color: e.target.value })} />
                                    </div>
                                    <div className="col-4">
                                        <Form.Label className="small mb-0">Colore sfondo</Form.Label>
                                        <Form.Control type="color" size="sm" value={slide.background_color || '#e5e7eb'} onChange={(e) => updateSlide(idx, { background_color: e.target.value })} />
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-6">
                                        <Form.Label className="small mb-0">Immagine desktop</Form.Label>
                                        <Form.Control type="file" size="sm" accept="image/*" onChange={(e) => handleSlideImage(idx, 'image_url', e.target.files[0])} />
                                        {slide.image_url && <Image src={slidePreviewUrl(slide.image_url)} thumbnail fluid className="mt-2" style={{ maxHeight: '90px' }} />}
                                    </div>
                                    <div className="col-md-6">
                                        <Form.Label className="small mb-0">Immagine mobile (opzionale)</Form.Label>
                                        <Form.Control type="file" size="sm" accept="image/*" onChange={(e) => handleSlideImage(idx, 'mobile_image_url', e.target.files[0])} />
                                        {slide.mobile_image_url && <Image src={slidePreviewUrl(slide.mobile_image_url)} thumbnail fluid className="mt-2" style={{ maxHeight: '90px' }} />}
                                    </div>
                                </div>
                            </div>
                        ))}
                        <Button variant="outline-primary" onClick={() => setSlides([...slides, { ...EMPTY_SLIDE }])}>+ Aggiungi slide</Button>
                    </>
                );
            case 'product_gallery':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Titolo Sezione</Form.Label>
                            <Form.Control
                                type="text"
                                name="gallery_title"
                                value={formData.content?.gallery_title || ''}
                                onChange={handleContentChange}
                                placeholder="es. 🎬 I Nostri Film, 🎵 Ultimi Arrivi, ⭐ In Vetrina"
                            />
                            <Form.Text>Puoi usare emoji nel titolo (es. 🎬 🎵 🎮 ⭐ 🔥 🎁)</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Tipo Prodotto</Form.Label>
                            <Form.Select
                                name="product_type"
                                value={formData.content?.product_type || 'film'}
                                onChange={handleContentChange}
                            >
                                <option value="film">🎬 Film</option>
                                <option value="musica">🎵 Musica</option>
                                <option value="collectible">🎮 Collezionabili</option>
                                <option value="servizi">⚙️ Servizi</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Modalità Selezione</Form.Label>
                            <Form.Select
                                name="selection_mode"
                                value={formData.content?.selection_mode || 'latest'}
                                onChange={handleContentChange}
                            >
                                <option value="latest">Ultime Novità</option>
                                <option value="showcase">In Vetrina</option>
                                <option value="promo">In Promozione</option>
                                <option value="filtered">Filtrata (specifica criteri)</option>
                            </Form.Select>
                        </Form.Group>

                        {/* ---- FILTRI (solo quando filtered) ---- */}
                        {formData.content?.selection_mode === 'filtered' && (
                            <>
                                <hr className="my-3" />
                                <h6 className="mb-3">🔍 Criteri di Filtro</h6>

                                {/* FILM */}
                                {formData.content?.product_type === 'film' && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Genere</Form.Label>
                                            <Form.Select value={formData.content?.filters?.genere || ''} onChange={(e) => handleFilterChange('genere', e.target.value)}>
                                                <option value="">Tutti i generi</option>
                                                {(filterOptions.genres || []).map(g => <option key={g} value={g}>{g}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Supporto</Form.Label>
                                            <Form.Select value={formData.content?.filters?.supporto || ''} onChange={(e) => handleFilterChange('supporto', e.target.value)}>
                                                <option value="">Tutti i supporti</option>
                                                {(filterOptions.supportos || []).map(s => <option key={s} value={s}>{s}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Condizione</Form.Label>
                                            <Form.Select value={formData.content?.filters?.condizione || ''} onChange={(e) => handleFilterChange('condizione', e.target.value)}>
                                                <option value="">Tutte le condizioni</option>
                                                {(filterOptions.conditions || []).map(c => <option key={c} value={c}>{c}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Regista</Form.Label>
                                            <Form.Control type="text" value={formData.content?.filters?.regista || ''} onChange={(e) => handleFilterChange('regista', e.target.value)} placeholder="es. Kubrick, Spielberg (separati da virgola)" list="gallery-registi-list" />
                                            <datalist id="gallery-registi-list">
                                                {filmSuggestions.registi.map(n => <option key={n} value={n} />)}
                                            </datalist>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Attori</Form.Label>
                                            <Form.Control type="text" value={formData.content?.filters?.attori || ''} onChange={(e) => handleFilterChange('attori', e.target.value)} placeholder="es. Tom Hanks, Meryl Streep (separati da virgola)" list="gallery-attori-list" />
                                            <datalist id="gallery-attori-list">
                                                {filmSuggestions.attori.map(n => <option key={n} value={n} />)}
                                            </datalist>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Casa di Produzione / Distribuzione</Form.Label>
                                            <Form.Control type="text" value={formData.content?.filters?.casa_produzione || ''} onChange={(e) => handleFilterChange('casa_produzione', e.target.value)} placeholder="es. Warner Bros, Universal (separati da virgola)" list="gallery-case-list" />
                                            <datalist id="gallery-case-list">
                                                {filmSuggestions.case.map(n => <option key={n} value={n} />)}
                                            </datalist>
                                        </Form.Group>
                                        <div className="row">
                                            <div className="col-6">
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Anno Da</Form.Label>
                                                    <Form.Control type="number" min="1900" max="2030" value={formData.content?.filters?.anno_min || ''} onChange={(e) => handleFilterChange('anno_min', e.target.value ? parseInt(e.target.value) : undefined)} placeholder="es. 1980" />
                                                </Form.Group>
                                            </div>
                                            <div className="col-6">
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Anno A</Form.Label>
                                                    <Form.Control type="number" min="1900" max="2030" value={formData.content?.filters?.anno_max || ''} onChange={(e) => handleFilterChange('anno_max', e.target.value ? parseInt(e.target.value) : undefined)} placeholder="es. 1990" />
                                                </Form.Group>
                                            </div>
                                        </div>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Fuori Catalogo</Form.Label>
                                            <Form.Select
                                                value={
                                                    formData.content?.filters?.fuori_catalogo_filter === true ? 'true'
                                                    : formData.content?.filters?.fuori_catalogo_filter === false ? 'false'
                                                    : ''
                                                }
                                                onChange={(e) => {
                                                    const v = e.target.value;
                                                    handleFilterChange('fuori_catalogo_filter', v === 'true' ? true : v === 'false' ? false : undefined);
                                                }}
                                            >
                                                <option value="">Solo in catalogo (default)</option>
                                                <option value="true">Solo fuori catalogo</option>
                                                <option value="false">Tutti (incluso fuori catalogo)</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </>
                                )}

                                {/* MUSICA */}
                                {formData.content?.product_type === 'musica' && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Genere</Form.Label>
                                            <Form.Select value={formData.content?.filters?.genere || ''} onChange={(e) => handleFilterChange('genere', e.target.value)}>
                                                <option value="">Tutti i generi</option>
                                                {(filterOptions.genres || []).map(g => <option key={g} value={g}>{g}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Supporto</Form.Label>
                                            <Form.Select value={formData.content?.filters?.supporto || ''} onChange={(e) => handleFilterChange('supporto', e.target.value)}>
                                                <option value="">Tutti i supporti</option>
                                                {(filterOptions.supportos || []).map(s => <option key={s} value={s}>{s}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Artista</Form.Label>
                                            <Form.Control type="text" value={formData.content?.filters?.artista || ''} onChange={(e) => handleFilterChange('artista', e.target.value)} placeholder="es. Beatles, Morricone" />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Casa Discografica</Form.Label>
                                            <Form.Control type="text" value={formData.content?.filters?.casa_produzione || ''} onChange={(e) => handleFilterChange('casa_produzione', e.target.value)} placeholder="es. EMI, Sony Music" />
                                        </Form.Group>
                                        <div className="row">
                                            <div className="col-6">
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Anno Da</Form.Label>
                                                    <Form.Control type="number" min="1900" max="2030" value={formData.content?.filters?.anno_min || ''} onChange={(e) => handleFilterChange('anno_min', e.target.value ? parseInt(e.target.value) : undefined)} placeholder="es. 1960" />
                                                </Form.Group>
                                            </div>
                                            <div className="col-6">
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Anno A</Form.Label>
                                                    <Form.Control type="number" min="1900" max="2030" value={formData.content?.filters?.anno_max || ''} onChange={(e) => handleFilterChange('anno_max', e.target.value ? parseInt(e.target.value) : undefined)} placeholder="es. 1980" />
                                                </Form.Group>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* COLLECTIBLE */}
                                {formData.content?.product_type === 'collectible' && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Categoria Prodotto</Form.Label>
                                            <Form.Select value={formData.content?.filters?.categoria_prodotto || ''} onChange={(e) => handleFilterChange('categoria_prodotto', e.target.value)}>
                                                <option value="">Tutte le categorie</option>
                                                {(filterOptions.categorie_prodotto || []).map(c => <option key={c} value={c}>{c}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Brand</Form.Label>
                                            <Form.Select value={formData.content?.filters?.brand || ''} onChange={(e) => handleFilterChange('brand', e.target.value)}>
                                                <option value="">Tutti i brand</option>
                                                {(filterOptions.brands || []).map(b => <option key={b} value={b}>{b}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Licenza</Form.Label>
                                            <Form.Select value={formData.content?.filters?.licenza || ''} onChange={(e) => handleFilterChange('licenza', e.target.value)}>
                                                <option value="">Tutte le licenze</option>
                                                {(filterOptions.licenze || []).map(l => <option key={l} value={l}>{l}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Personaggio</Form.Label>
                                            <Form.Select value={formData.content?.filters?.personaggio || ''} onChange={(e) => handleFilterChange('personaggio', e.target.value)}>
                                                <option value="">Tutti i personaggi</option>
                                                {(filterOptions.personaggi || []).map(p => <option key={p} value={p}>{p}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="switch"
                                                id="gallery-collectible-in_vetrina-switch"
                                                label="Solo In Vetrina"
                                                checked={formData.content?.filters?.in_vetrina || false}
                                                onChange={(e) => handleFilterChange('in_vetrina', e.target.checked || undefined)}
                                            />
                                        </Form.Group>
                                    </>
                                )}

                                {/* SERVIZI */}
                                {formData.content?.product_type === 'servizi' && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Categoria</Form.Label>
                                            <Form.Select value={formData.content?.filters?.categoria || ''} onChange={(e) => handleFilterChange('categoria', e.target.value)}>
                                                <option value="">Tutte le categorie</option>
                                                {(filterOptions.categorie_prodotto || []).map(c => <option key={c} value={c}>{c}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Supporto</Form.Label>
                                            <Form.Select value={formData.content?.filters?.supporto || ''} onChange={(e) => handleFilterChange('supporto', e.target.value)}>
                                                <option value="">Tutti i supporti</option>
                                                {(filterOptions.supportos || []).map(s => <option key={s} value={s}>{s}</option>)}
                                            </Form.Select>
                                        </Form.Group>
                                    </>
                                )}

                                {/* Fascia prezzo (non per servizi che usa prezzo_indicativo_da) */}
                                {formData.content?.product_type !== 'servizi' && (
                                    <div className="row">
                                        <div className="col-6">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Prezzo Min (€)</Form.Label>
                                                <Form.Control type="number" min="0" step="0.01" value={formData.content?.filters?.prezzo_min || ''} onChange={(e) => handleFilterChange('prezzo_min', e.target.value ? parseFloat(e.target.value) : undefined)} placeholder="es. 5.00" />
                                            </Form.Group>
                                        </div>
                                        <div className="col-6">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Prezzo Max (€)</Form.Label>
                                                <Form.Control type="number" min="0" step="0.01" value={formData.content?.filters?.prezzo_max || ''} onChange={(e) => handleFilterChange('prezzo_max', e.target.value ? parseFloat(e.target.value) : undefined)} placeholder="es. 50.00" />
                                            </Form.Group>
                                        </div>
                                    </div>
                                )}
                                <hr className="my-3" />
                            </>
                        )}

                        {/* Ordinamento */}
                        <Form.Group className="mb-3">
                            <Form.Label>Ordinamento</Form.Label>
                            <Form.Select name="sort_by" value={formData.content?.sort_by || 'id'} onChange={handleContentChange}>
                                <option value="id">Ultimi inseriti (default)</option>
                                <option value="price_asc">Prezzo crescente</option>
                                <option value="price_desc">Prezzo decrescente</option>
                                <option value="random">Casuale (diversi a ogni visita)</option>
                                {formData.content?.product_type !== 'servizi' && (
                                    <option value="rating">Rating (migliori prima)</option>
                                )}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Numero Prodotti in Galleria</Form.Label>
                            <Form.Control
                                type="number"
                                min="1"
                                max="24"
                                value={formData.content?.limit || 6}
                                onChange={(e) => handleContentChange({ target: { name: 'limit', value: parseInt(e.target.value) || 6 } })}
                            />
                            <Form.Text>Quanti prodotti mostrare in griglia (max 24, default 6). Su desktop vengono mostrate sempre 6 colonne.</Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                id="gallery-only_available-switch"
                                label="Solo Prodotti Disponibili (quantità &gt; 0)"
                                checked={formData.content?.only_available !== false}
                                onChange={(e) => handleContentChange({ target: { name: 'only_available', value: e.target.checked } })}
                            />
                        </Form.Group>
                    </>
                );
            case 'product_carousel':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Titolo Sezione</Form.Label>
                            <Form.Control 
                                type="text" 
                                name="section_title" 
                                value={formData.content?.section_title || ''} 
                                onChange={handleContentChange}
                                placeholder="es. I Nostri Film"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Tipo Prodotto</Form.Label>
                            <Form.Select 
                                name="product_type" 
                                value={formData.content?.product_type || 'film'} 
                                onChange={handleContentChange}
                                required
                            >
                                <option value="film">Film</option>
                                <option value="musica">Musica</option>
                                <option value="collectible">Collezionabili</option>
                                <option value="servizi">Servizi</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Modalità Selezione</Form.Label>
                            <Form.Select 
                                name="selection_mode" 
                                value={formData.content?.selection_mode || 'showcase'} 
                                onChange={handleContentChange}
                            >
                                <option value="manual">Manuale (seleziona ID specifici)</option>
                                <option value="latest">Ultime Novità</option>
                                <option value="showcase">In Vetrina</option>
                                <option value="promo">In Promozione</option>
                                <option value="filtered">Filtrata (specifica criteri)</option>
                                <option value="carousel">Carousel Avanzato (con filtri)</option>
                            </Form.Select>
                        </Form.Group>
                        {formData.content?.selection_mode === 'manual' && (
                            <Form.Group className="mb-3">
                                <Form.Label>ID Prodotti (separati da virgola)</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    name="product_ids_string"
                                    value={formData.content?.product_ids ? formData.content.product_ids.join(',') : ''} 
                                    onChange={(e) => {
                                        const idsString = e.target.value;
                                        const idsArray = idsString.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
                                        handleContentChange({ target: { name: 'product_ids', value: idsArray } });
                                    }}
                                    placeholder="es. 1,5,12,23"
                                />
                                <Form.Text>Inserisci gli ID dei prodotti separati da virgola. Es: 1,5,12,23</Form.Text>
                            </Form.Group>
                        )}
                        {formData.content?.selection_mode === 'filtered' && (
                            <>
                                <hr className="my-3" />
                                <h6 className="mb-3">Criteri di Filtro</h6>
                                
                                {/* Filtri per Film */}
                                {formData.content?.product_type === 'film' && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Supporto</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.supporto || ''} 
                                                onChange={(e) => handleFilterChange('supporto', e.target.value)}
                                            >
                                                <option value="">Tutti i supporti</option>
                                                {(filterOptions.supportos || []).map(support => (
                                                    <option key={support} value={support}>{support}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Condizione</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.condizione || ''} 
                                                onChange={(e) => handleFilterChange('condizione', e.target.value)}
                                            >
                                                <option value="">Tutte le condizioni</option>
                                                {(filterOptions.conditions || []).map(condition => (
                                                    <option key={condition} value={condition}>{condition}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Genere</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.genere || ''} 
                                                onChange={(e) => handleFilterChange('genere', e.target.value)}
                                            >
                                                <option value="">Tutti i generi</option>
                                                {(filterOptions.genres || []).map(genre => (
                                                    <option key={genre} value={genre}>{genre}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </>
                                )}

                                {/* Filtri per Musica */}
                                {formData.content?.product_type === 'musica' && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Supporto</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.supporto || ''} 
                                                onChange={(e) => handleFilterChange('supporto', e.target.value)}
                                            >
                                                <option value="">Tutti i supporti</option>
                                                {(filterOptions.supportos || []).map(support => (
                                                    <option key={support} value={support}>{support}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Condizione</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.condizione || ''} 
                                                onChange={(e) => handleFilterChange('condizione', e.target.value)}
                                            >
                                                <option value="">Tutte le condizioni</option>
                                                {(filterOptions.conditions || []).map(condition => (
                                                    <option key={condition} value={condition}>{condition}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Genere</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.genere || ''} 
                                                onChange={(e) => handleFilterChange('genere', e.target.value)}
                                            >
                                                <option value="">Tutti i generi</option>
                                                {(filterOptions.genres || []).map(genre => (
                                                    <option key={genre} value={genre}>{genre}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </>
                                )}

                                {/* Filtri per Collectibles */}
                                {formData.content?.product_type === 'collectible' && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Categoria Prodotto</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.categoria_prodotto || ''} 
                                                onChange={(e) => handleFilterChange('categoria_prodotto', e.target.value)}
                                            >
                                                <option value="">Tutte le categorie</option>
                                                {(filterOptions.categorie_prodotto || []).map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Brand</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.brand || ''} 
                                                onChange={(e) => handleFilterChange('brand', e.target.value)}
                                            >
                                                <option value="">Tutti i brand</option>
                                                {(filterOptions.brands || []).map(brand => (
                                                    <option key={brand} value={brand}>{brand}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Licenza</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.licenza || ''} 
                                                onChange={(e) => handleFilterChange('licenza', e.target.value)}
                                            >
                                                <option value="">Tutte le licenze</option>
                                                {(filterOptions.licenze || []).map(lic => (
                                                    <option key={lic} value={lic}>{lic}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Personaggio</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.personaggio || ''} 
                                                onChange={(e) => handleFilterChange('personaggio', e.target.value)}
                                            >
                                                <option value="">Tutti i personaggi</option>
                                                {(filterOptions.personaggi || []).map(pers => (
                                                    <option key={pers} value={pers}>{pers}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </>
                                )}

                                {/* Filtri per Servizi */}
                                {formData.content?.product_type === 'servizi' && (
                                    <>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Supporto</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.supporto || ''} 
                                                onChange={(e) => handleFilterChange('supporto', e.target.value)}
                                            >
                                                <option value="">Tutti i supporti</option>
                                                {(filterOptions.supportos || []).map(support => (
                                                    <option key={support} value={support}>{support}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Categoria</Form.Label>
                                            <Form.Select 
                                                value={formData.content?.filters?.categoria || ''} 
                                                onChange={(e) => handleFilterChange('categoria', e.target.value)}
                                            >
                                                <option value="">Tutte le categorie</option>
                                                {(filterOptions.categorie_prodotto || []).map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </>
                                )}

                                {/* Filtri comuni: Fascia di Prezzo */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Prezzo Minimo (€)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        min="0"
                                        step="0.01"
                                        value={formData.content?.filters?.prezzo_min || ''} 
                                        onChange={(e) => handleFilterChange('prezzo_min', e.target.value ? parseFloat(e.target.value) : undefined)}
                                        placeholder="es. 5.00"
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Prezzo Massimo (€)</Form.Label>
                                    <Form.Control 
                                        type="number" 
                                        min="0"
                                        step="0.01"
                                        value={formData.content?.filters?.prezzo_max || ''} 
                                        onChange={(e) => handleFilterChange('prezzo_max', e.target.value ? parseFloat(e.target.value) : undefined)}
                                        placeholder="es. 50.00"
                                    />
                                </Form.Group>
                                <hr className="my-3" />
                            </>
                        )}
                        {formData.content?.selection_mode === 'carousel' && (
                            <>
                                <hr className="my-3" />
                                <h6 className="mb-3">Filtri Carousel Avanzato</h6>
                                
                                {/* Filtro Solo Disponibili - comune a tutti */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="only_available-switch"
                                        label="Solo Prodotti Disponibili (quantità > 0)"
                                        checked={formData.content?.filters?.only_available || false}
                                        onChange={(e) => handleFilterChange('only_available', e.target.checked)}
                                    />
                                </Form.Group>

                                {/* Casa di Produzione per Film e Musica */}
                                {(formData.content?.product_type === 'film' || formData.content?.product_type === 'musica') && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Casa di Produzione/Distribuzione</Form.Label>
                                        <Form.Control 
                                            type="text"
                                            value={formData.content?.filters?.casa_produzione || ''} 
                                            onChange={(e) => handleFilterChange('casa_produzione', e.target.value)}
                                            placeholder="es. Warner Bros, Universal, Sony"
                                        />
                                        <Form.Text>Filtra per casa di produzione o distribuzione (cerca in entrambi i campi)</Form.Text>
                                    </Form.Group>
                                )}

                                {/* Brand per Collectibles */}
                                {formData.content?.product_type === 'collectible' && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Brand/Licenza</Form.Label>
                                        <Form.Control 
                                            type="text"
                                            value={formData.content?.filters?.casa_produzione || ''} 
                                            onChange={(e) => handleFilterChange('casa_produzione', e.target.value)}
                                            placeholder="es. Marvel, DC Comics, Star Wars"
                                        />
                                        <Form.Text>Filtra per brand o licenza (cerca in entrambi i campi)</Form.Text>
                                    </Form.Group>
                                )}

                                {/* Promozione attiva */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="in_promo-switch"
                                        label="Solo Prodotti in Promozione"
                                        checked={formData.content?.filters?.in_promo || false}
                                        onChange={(e) => handleFilterChange('in_promo', e.target.checked || undefined)}
                                    />
                                </Form.Group>

                                <hr className="my-3" />
                                <h6 className="mb-3">Impostazioni Carousel</h6>

                                {/* Auto-scroll */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="auto_scroll-switch"
                                        label="Scorrimento Automatico"
                                        checked={formData.content?.carousel_settings?.auto_scroll || false}
                                        onChange={(e) => {
                                            const settings = formData.content?.carousel_settings || {};
                                            handleContentChange({ 
                                                target: { 
                                                    name: 'carousel_settings', 
                                                    value: { ...settings, auto_scroll: e.target.checked }
                                                }
                                            });
                                        }}
                                    />
                                </Form.Group>

                                {/* Delay auto-scroll */}
                                {formData.content?.carousel_settings?.auto_scroll && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Ritardo Scorrimento Automatico (ms)</Form.Label>
                                        <Form.Control 
                                            type="number"
                                            min="1000"
                                            step="500"
                                            value={formData.content?.carousel_settings?.auto_scroll_delay || 5000}
                                            onChange={(e) => {
                                                const settings = formData.content?.carousel_settings || {};
                                                handleContentChange({ 
                                                    target: { 
                                                        name: 'carousel_settings', 
                                                        value: { ...settings, auto_scroll_delay: parseInt(e.target.value) }
                                                    }
                                                });
                                            }}
                                            placeholder="5000"
                                        />
                                        <Form.Text>Tempo in millisecondi tra uno scorrimento e il successivo (default: 5000ms = 5 secondi)</Form.Text>
                                    </Form.Group>
                                )}

                                {/* Badge disponibilità */}
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="show_availability_badge-switch"
                                        label="Mostra Badge Disponibilità"
                                        checked={formData.content?.carousel_settings?.show_availability_badge || false}
                                        onChange={(e) => {
                                            const settings = formData.content?.carousel_settings || {};
                                            handleContentChange({ 
                                                target: { 
                                                    name: 'carousel_settings', 
                                                    value: { ...settings, show_availability_badge: e.target.checked }
                                                }
                                            });
                                        }}
                                    />
                                    <Form.Text>Mostra un badge verde/rosso per indicare la disponibilità del prodotto</Form.Text>
                                </Form.Group>

                                <hr className="my-3" />
                                <h6 className="mb-3">Numero Prodotti</h6>

                                {/* Limit totale */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Numero Totale Prodotti nel Carousel</Form.Label>
                                    <Form.Control 
                                        type="number"
                                        min="1"
                                        max="50"
                                        value={formData.content?.limit || 12}
                                        onChange={(e) => {
                                            handleContentChange({ 
                                                target: { 
                                                    name: 'limit', 
                                                    value: parseInt(e.target.value) || 12
                                                }
                                            });
                                        }}
                                    />
                                    <Form.Text>Quanti prodotti caricare e far scorrere nel carousel (default: 12). I "Prodotti Visibili" sotto controllano solo quanti vederne contemporaneamente.</Form.Text>
                                </Form.Group>

                                <hr className="my-3" />
                                <h6 className="mb-3">Layout Responsivo</h6>

                                {/* Items Desktop */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Prodotti Visibili (Desktop - &gt;1200px)</Form.Label>
                                    <Form.Control 
                                        type="number"
                                        min="1"
                                        max="8"
                                        value={formData.content?.carousel_settings?.items_desktop || 5}
                                        onChange={(e) => {
                                            const settings = formData.content?.carousel_settings || {};
                                            handleContentChange({ 
                                                target: { 
                                                    name: 'carousel_settings', 
                                                    value: { ...settings, items_desktop: parseInt(e.target.value) }
                                                }
                                            });
                                        }}
                                    />
                                    <Form.Text>Numero di prodotti visibili su schermi desktop (default: 5)</Form.Text>
                                </Form.Group>

                                {/* Items Tablet */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Prodotti Visibili (Tablet - 768-1199px)</Form.Label>
                                    <Form.Control 
                                        type="number"
                                        min="1"
                                        max="5"
                                        value={formData.content?.carousel_settings?.items_tablet || 3}
                                        onChange={(e) => {
                                            const settings = formData.content?.carousel_settings || {};
                                            handleContentChange({ 
                                                target: { 
                                                    name: 'carousel_settings', 
                                                    value: { ...settings, items_tablet: parseInt(e.target.value) }
                                                }
                                            });
                                        }}
                                    />
                                    <Form.Text>Numero di prodotti visibili su tablet (default: 3)</Form.Text>
                                </Form.Group>

                                {/* Items Mobile */}
                                <Form.Group className="mb-3">
                                    <Form.Label>Prodotti Visibili (Mobile - &lt;768px)</Form.Label>
                                    <Form.Control 
                                        type="number"
                                        min="1"
                                        max="3"
                                        value={formData.content?.carousel_settings?.items_mobile || 1}
                                        onChange={(e) => {
                                            const settings = formData.content?.carousel_settings || {};
                                            handleContentChange({ 
                                                target: { 
                                                    name: 'carousel_settings', 
                                                    value: { ...settings, items_mobile: parseInt(e.target.value) }
                                                }
                                            });
                                        }}
                                    />
                                    <Form.Text>Numero di prodotti visibili su dispositivi mobili (default: 1)</Form.Text>
                                </Form.Group>

                                <hr className="my-3" />
                            </>
                        )}
                    </>
                );
            default:
                return <p>Seleziona un tipo di blocco per vedere le opzioni.</p>;
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{isNew ? 'Nuovo Blocco Homepage' : 'Modifica Blocco Homepage'}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Titolo Blocco (interno)</Form.Label>
                        <Form.Control type="text" name="title" value={formData.title || ''} onChange={handleChange} required />
                        <Form.Text>Questo titolo è solo per riferimento interno.</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Tipo Blocco</Form.Label>
                        <Form.Select name="block_type" value={formData.block_type || ''} onChange={handleChange} required disabled={!isNew}>
                            <option value="hero_banner">Hero Banner</option>
                            <option value="hero_carousel">Hero Carosello (slide)</option>
                            <option value="benefits_bar">Fascia Vantaggi (icone)</option>
                            <option value="product_gallery">Galleria Prodotti</option>
                            <option value="product_carousel">Carousel Prodotti</option>
                        </Form.Select>
                        {!isNew && <Form.Text>Il tipo di blocco non può essere modificato.</Form.Text>}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Check type="switch" id="is_active-switch" label="Blocco Attivo" name="is_active" checked={formData.is_active || false} onChange={handleChange} />
                    </Form.Group>
                    <hr />
                    <h5>Contenuto del Blocco</h5>
                    {renderContentFields()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Annulla
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Salva'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default HomepageBlockForm;
